const getMaterialKey = (detail) => {
  const name =
    detail.bid_material_type_name ??
    detail.general_rfq_material_type_name ??
    detail.rfq_material_type_name ??
    "";
  const subCategory =
    detail.bid_material_type_sub_category ??
    detail.general_rfq_material_type_sub_category ??
    "";
  return `${name}::${subCategory}`;
};

const getMaterialLineQty = (detail) =>
  Number(
    detail.bid_material_quantity ??
      detail.general_rfq_material_type_total_quantity ??
      detail.rfq_material_type_total_quantity ??
      0,
  ) || 0;

const getMaterialUom = (detail) =>
  detail.bid_uom ?? detail.general_rfq_material_type_uom ?? "";

const getMaterialName = (detail) =>
  detail.bid_material_type_name ??
  detail.general_rfq_material_type_name ??
  detail.rfq_material_type_name ??
  "";

const getMaterialSubCategory = (detail) =>
  detail.bid_material_type_sub_category ??
  detail.general_rfq_material_type_sub_category ??
  "";

const mergeVendorIntoMaterial = (material, detail, lineQty) => {
  (detail.vendor ?? []).forEach((vendor) => {
    const vendorId = vendor.vendor_id;
    if (!material.vendorBidsMap.has(vendorId)) {
      material.vendorBidsMap.set(vendorId, {
        vendorId,
        vendorName: vendor.vendor_name,
        bidRfqIds: [],
        unitRateQtySum: 0,
        total_amount_without_gst: 0,
        total_amount_with_gst: 0,
        vendor_checked: false,
        select_reason: null,
        comparison_ranking: vendor.comparison_ranking,
        other_reason_text: vendor.other_reason_text || "",
      });
    }

    const vendorBid = material.vendorBidsMap.get(vendorId);
    const qty = lineQty || 0;
    const unitRate = Number(vendor.unit_rate) || 0;

    vendorBid.bidRfqIds.push(vendor.bid_rfq_id);
    vendorBid.unitRateQtySum += unitRate * qty;
    vendorBid.total_amount_without_gst +=
      Number(vendor.amount_without_gst) || 0;
    vendorBid.total_amount_with_gst += Number(vendor.amount_with_gst) || 0;

    if (vendor.is_selected) {
      vendorBid.vendor_checked = true;
      vendorBid.select_reason = vendor.selected_reason;
      vendorBid.comparison_ranking = vendor.comparison_ranking;
      vendorBid.other_reason_text = vendor.other_reason_text || "";
    }
  });
};

const parseComparisonRank = (rank) => {
  const match = String(rank ?? "").match(/\d+/);
  return match ? Number(match[0]) : 999;
};

const extractVendorGrandTotals = (data, vendorNameById) => {
  const totalsMap = new Map();

  const upsertTotal = (entry) => {
    const vendorId = entry.vendor_id;
    if (vendorId == null) return;

    totalsMap.set(vendorId, {
      vendorId,
      vendorName: vendorNameById.get(vendorId) ?? "",
      totalAmount: Number(entry.amount_without_gst) || 0,
      totalAmountWithGST: Number(entry.amount_with_gst) || 0,
      comparison_ranking: entry.comparison_ranking ?? null,
    });
  };

  (data.purchase_order_city_details ?? []).forEach((city) => {
    (city.city_total ?? []).forEach(upsertTotal);

    (city.purchase_order_site_details ?? []).forEach((site) => {
      (site.vendor_sub_total ?? []).forEach((entry) => {
        if (!totalsMap.has(entry.vendor_id)) {
          upsertTotal(entry);
        }
      });
    });
  });

  return Array.from(totalsMap.values()).sort(
    (a, b) =>
      parseComparisonRank(a.comparison_ranking) -
        parseComparisonRank(b.comparison_ranking) ||
      a.totalAmountWithGST - b.totalAmountWithGST,
  );
};

const resolveIsSameVendor = (data) => {
  const cities = data.purchase_order_city_details ?? [];
  if (cities.some((city) => city.is_same_vendor === false)) {
    return false;
  }
  if (cities.some((city) => city.is_same_vendor === true)) {
    return true;
  }
  return null;
};

export function parseGeneralSummaryComparativeResponse(apiResponse) {
  const data = apiResponse?.data ?? apiResponse ?? {};
  const materialMap = new Map();

  const upsertMaterialDetail = (detail) => {
    const key = getMaterialKey(detail);
    if (!materialMap.has(key)) {
      materialMap.set(key, {
        key: materialMap.size,
        materialName: getMaterialName(detail),
        subCategory: getMaterialSubCategory(detail),
        quantity: 0,
        uom: getMaterialUom(detail),
        vendorBidsMap: new Map(),
      });
    }

    const material = materialMap.get(key);
    const lineQty = getMaterialLineQty(detail);
    material.quantity += lineQty;
    mergeVendorIntoMaterial(material, detail, lineQty);
  };

  (data.purchase_order_material_details ?? []).forEach(upsertMaterialDetail);

  (data.purchase_order_city_details ?? []).forEach((city) => {
    (city.purchase_order_site_details ?? []).forEach((site) => {
      (site.bid_rfq_detail ?? []).forEach(upsertMaterialDetail);
    });
  });

  const vendorNameById = new Map();
  materialMap.forEach((material) => {
    material.vendorBidsMap.forEach((vendorBid, vendorId) => {
      if (!vendorNameById.has(vendorId)) {
        vendorNameById.set(vendorId, vendorBid.vendorName);
      }
    });
  });

  const materials = Array.from(materialMap.values()).map((material) => {
    const vendorBids = Array.from(material.vendorBidsMap.values()).map(
      (vendorBid) => {
        const unitRate =
          material.quantity > 0 && vendorBid.unitRateQtySum > 0
            ? vendorBid.unitRateQtySum / material.quantity
            : material.quantity > 0
              ? vendorBid.total_amount_without_gst / material.quantity
              : 0;

        return {
          ...vendorBid,
          bidRfqId: vendorBid.bidRfqIds[0] ?? null,
          r_price: unitRate,
          unitRate,
        };
      },
    );

    return {
      key: material.key,
      materialName: material.materialName,
      subCategory: material.subCategory,
      quantity: material.quantity,
      uom: material.uom,
      vendorBids,
    };
  });

  return {
    floatId: data.float_rfq ?? data.general_rfq ?? data.id ?? null,
    company: data.company,
    rfqNumber: data.rfq_number,
    poApproval: Boolean(data.po_approval),
    otherReason: Boolean(data.other_reason),
    purchaseOrderPattern: data.purchase_order_pattern,
    isSameVendor: resolveIsSameVendor(data),
    materials,
    vendorGrandTotals: extractVendorGrandTotals(data, vendorNameById),
    rawCityDetails: data.purchase_order_city_details ?? [],
    totalAmount: Number(data.total_amount) || 0,
  };
}

export const getGeneralMaterialRowKey = (material) =>
  `${material.materialName}::${material.subCategory}`;

const findSelectedVendorBidForMaterial = (material) =>
  material?.vendorBids?.find((vendorBid) => vendorBid.vendor_checked);

const buildMaterialPayloadEntry = (vendorBid, overrides = {}) => {
  const select_reason = overrides.select_reason ?? vendorBid.select_reason ?? null;

  return {
    vendor: vendorBid.vendorId,
    comparison_ranking:
      overrides.comparison_ranking ?? vendorBid.comparison_ranking ?? "L1",
    select_reason,
    other_reason_text:
      select_reason === "Others"
        ? overrides.other_reason_text ??
          vendorBid.other_reason_text ??
          null
        : null,
    general_bid_rfq_detail: (
      vendorBid.bidRfqIds ?? [vendorBid.bidRfqId]
    ).filter(Boolean),
    amount_without_gst: vendorBid.total_amount_without_gst || 0,
    amount_with_gst: vendorBid.total_amount_with_gst || 0,
  };
};

const getComparisonRankingForMaterial = (material, vendorId) => {
  const vendorBid = material.vendorBids.find((bid) => bid.vendorId === vendorId);
  if (vendorBid?.comparison_ranking) {
    return vendorBid.comparison_ranking;
  }

  const sorted = [...material.vendorBids].sort(
    (a, b) =>
      (a.total_amount_with_gst || 0) - (b.total_amount_with_gst || 0),
  );
  const rankIndex = sorted.findIndex((bid) => bid.vendorId === vendorId);
  return rankIndex >= 0 ? `L${rankIndex + 1}` : "L1";
};

const buildMaterialsPayload = ({
  vendorMode,
  selectedVendorId,
  materials,
  vendorGrandTotals,
}) => {
  const payloadMaterials = [];

  if (vendorMode === "common") {
    const vendorTotal = vendorGrandTotals?.find(
      (vendor) => vendor.vendorId === selectedVendorId,
    );
    const selectedVendorBid = materials
      .flatMap((material) => material.vendorBids)
      .find((vendorBid) => vendorBid.vendorId === selectedVendorId);

    const comparison_ranking =
      vendorTotal?.comparison_ranking ??
      selectedVendorBid?.comparison_ranking ??
      "L1";
    const select_reason = selectedVendorBid?.select_reason ?? null;
    const other_reason_text =
      select_reason === "Others"
        ? selectedVendorBid?.other_reason_text || null
        : null;

    materials.forEach((material) => {
      const vendorBid = material.vendorBids.find(
        (bid) => bid.vendorId === selectedVendorId,
      );
      if (!vendorBid) return;

      payloadMaterials.push(
        buildMaterialPayloadEntry(vendorBid, {
          comparison_ranking,
          select_reason,
          other_reason_text,
        }),
      );
    });

    return payloadMaterials;
  }

  materials.forEach((material) => {
    const selectedBid = findSelectedVendorBidForMaterial(material);
    if (!selectedBid) return;

    payloadMaterials.push(
      buildMaterialPayloadEntry(selectedBid, {
        comparison_ranking: getComparisonRankingForMaterial(
          material,
          selectedBid.vendorId,
        ),
      }),
    );
  });

  return payloadMaterials;
};

export function buildGeneralPoOrderSubmitPayload({
  company,
  generalRfq,
  vendorMode = "common",
  selectedVendorId,
  materials,
  vendorGrandTotals,
}) {
  return {
    company,
    general_rfq: generalRfq,
    materials: buildMaterialsPayload({
      vendorMode,
      selectedVendorId,
      materials,
      vendorGrandTotals,
    }),
    is_same_vendor: vendorMode === "common",
  };
}

export function buildGeneralPoApprovalUrl(token, id, poStatus) {
  const encodedStatus = encodeURIComponent(poStatus ?? "");
  return `general-po-order/purchase-order-approval/${token}/${id}/?po_status=${encodedStatus}`;
}

export function buildGeneralPoVendorApprovalUrl(token, id, poStatus, vendorId) {
  const encodedStatus = encodeURIComponent(poStatus ?? "");
  return `general-po-order/po-approval-by-vendor/${token}/${id}/?po_status=${encodedStatus}&vendor_id=${vendorId}`;
}

const parsePreviewNumber = (value) => {
  if (typeof value === "number") return value;
  const parsed = Number(String(value ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapPreviewMaterials = (materials = []) =>
  materials.map((material) => {
    const amountWithoutGst = Number(material.amount_without_gst) || 0;
    const amountWithGst = Number(material.amount_with_gst) || 0;
    const unitRate = parsePreviewNumber(material.unit_rate);

    return {
      materialName:
        material.bid_material_type_name ??
        material.general_rfq_material_type_name ??
        "",
      quantity:
        material.bid_material_quantity ??
        material.general_rfq_material_type_total_quantity ??
        "",
      uom: material.bid_uom ?? material.general_rfq_material_type_uom ?? "",
      r_unit_price: unitRate,
      unitRateDisplay:
        typeof material.unit_rate === "string" && material.unit_rate.includes("₹")
          ? material.unit_rate
          : `₹ ${unitRate.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
      r_total_price: amountWithoutGst,
      total_amount_with_gst: amountWithGst,
      totalAmountDisplay:
        material.formatted_amount_without_gst ??
        `₹ ${amountWithoutGst.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      totalAmountGstDisplay:
        material.formatted_amount_with_gst ??
        `₹ ${amountWithGst.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    };
  });

const sumAmounts = (entries = []) => ({
  withGst: entries.reduce(
    (acc, entry) => acc + (Number(entry.amount_with_gst) || 0),
    0,
  ),
  withoutGst: entries.reduce(
    (acc, entry) => acc + (Number(entry.amount_without_gst) || 0),
    0,
  ),
});

export function parseGeneralPreviewAndSendApprovalResponse(apiResponse) {
  const data = apiResponse?.data ?? apiResponse ?? {};
  const responseData = data.purchase_order_city_details ?? [];

  const tableData = responseData.map((cityItem, index) => {
    if (cityItem.po_material_detail?.length) {
      const materials = mapPreviewMaterials(cityItem.po_material_detail);
      const vendorTotalFromCity = Number(
        cityItem.vendor_city_total?.[0]?.amount_with_gst,
      );
      const vendorTotalWithoutFromCity = Number(
        cityItem.vendor_city_total?.[0]?.amount_without_gst,
      );
      const vendorTotal =
        vendorTotalFromCity ||
        Number(cityItem.vendor_total) ||
        materials.reduce(
          (acc, material) => acc + (material.total_amount_with_gst || 0),
          0,
        );
      const vendorTotalWithout =
        vendorTotalWithoutFromCity ||
        materials.reduce(
          (acc, material) => acc + (material.r_total_price || 0),
          0,
        );

      return {
        key: `vendor-${cityItem.vendor_id ?? index}`,
        cityName: "",
        citySubTotal: vendorTotal,
        citySubTotalWithoutGST: vendorTotalWithout,
        isMaterialWise: true,
        comparative: [
          {
            siteName: "",
            siteId: null,
            siteSubTotal: vendorTotalWithout,
            siteSubTotalWithGST: vendorTotal,
            vendorWise: [
              {
                vendorName: cityItem.vendor_name ?? "",
                rank: {
                  comparison_ranking: cityItem.comparison_ranking,
                  selected_seasons:
                    cityItem.selected_seasons ?? cityItem.select_reason,
                  other_reason_text: cityItem.other_reason_text,
                },
                siteLocations: materials,
              },
            ],
          },
        ],
      };
    }

    const cityTotals = cityItem.vendor_city_total ?? cityItem.city_total ?? [];
    const { withGst, withoutGst } = sumAmounts(cityTotals);

    return {
      key: cityItem.po_city_name ?? cityItem.bid_rfq_city_name ?? `city-${index}`,
      cityName: cityItem.po_city_name ?? cityItem.bid_rfq_city_name ?? "",
      citySubTotal: withGst,
      citySubTotalWithoutGST: withoutGst,
      isMaterialWise: false,
      comparative: (cityItem.purchase_order_site_details ?? []).map((site) => {
        const siteTotals =
          site.vendor_site_sub_total ?? site.vendor_sub_total ?? [];
        const siteSubTotal = Number(siteTotals[0]?.amount_without_gst) || 0;
        const siteSubTotalWithGST = Number(siteTotals[0]?.amount_with_gst) || 0;

        if (site.po_vendor_detail?.length) {
          return {
            siteName: site.rfq_site_name ?? site.site_name ?? "",
            siteId: site.rfq_site_id ?? site.site_id,
            siteSubTotal,
            siteSubTotalWithGST,
            vendorWise: site.po_vendor_detail.map((vendorDetail) => ({
              vendorName: vendorDetail.vendor_name,
              rank: {
                comparison_ranking: vendorDetail.comparison_ranking,
                selected_seasons:
                  vendorDetail.selected_seasons ?? vendorDetail.select_reason,
                other_reason_text: vendorDetail.other_reason_text,
              },
              siteLocations: mapPreviewMaterials(
                vendorDetail.po_material_detail ??
                  vendorDetail.general_po_material_detail ??
                  [],
              ),
            })),
          };
        }

        const cityVendorRank = {
          comparison_ranking: cityItem.comparison_ranking,
          selected_seasons: cityItem.select_reason,
          other_reason_text: cityItem.other_reason_text,
        };

        return {
          siteName: site.rfq_site_name ?? site.site_name ?? "",
          siteId: site.rfq_site_id ?? site.site_id,
          siteSubTotal:
            siteSubTotal ||
            Number(site.amount_without_gst) ||
            Number(cityItem.amount_without_gst) ||
            0,
          siteSubTotalWithGST:
            siteSubTotalWithGST ||
            Number(site.amount_with_gst) ||
            Number(cityItem.amount_with_gst) ||
            0,
          vendorWise: [
            {
              vendorName:
                cityItem.vendor_name ??
                data.vendor_name ??
                data.selected_vendor_name ??
                "",
              rank: cityVendorRank,
              siteLocations: mapPreviewMaterials(site.bid_rfq_detail ?? []),
            },
          ],
        };
      }),
    };
  });

  const computedTotal = tableData.reduce(
    (total, cityItem) => total + (cityItem.citySubTotal || 0),
    0,
  );

  return {
    generalRfq: data.general_rfq ?? data.float_rfq ?? data.id ?? null,
    rfqNumber: data.rfq_number,
    buyer: data.buyer_name ?? data.company?.name ?? null,
    companyName: data.company_name ?? data.company?.name,
    firstApprover: data.first_approver_name,
    secondApprover: data.second_approver_name,
    poTotalAmount: Number(data.total_amount) || computedTotal,
    poApproval: Boolean(data.po_approval),
    isMaterialWise: tableData.every((item) => item.isMaterialWise),
    tableData,
  };
}

const formatPoAddress = (...parts) =>
  parts.filter((part) => part != null && String(part).trim() !== "").join(", ") ||
  "N/A";

const mapPoMaterialToRfqItem = (material) => {
  const qty = Number(
    material.bid_material_quantity ??
      material.general_rfq_material_type_total_quantity ??
      material.qty ??
      0,
  );
  const unitPrice = parsePreviewNumber(
    material.unit_rate ?? material.basic_unit_price ?? 0,
  );
  const basicAmount =
    Number(material.amount_without_gst ?? material.basic_amount) ||
    qty * unitPrice;

  return {
    material_name:
      material.bid_material_type_name ??
      material.general_rfq_material_type_name ??
      material.material_name ??
      "",
    hsn_code:
      material.hsn_code ??
      material.general_rfq_material_type_hsn ??
      material.hsn ??
      "",
    qty,
    uom:
      material.bid_uom ??
      material.general_rfq_material_type_uom ??
      material.uom ??
      "",
    delivery_date: material.delivery_date ?? "",
    tax: material.tax ?? material.tax_percentage ?? "",
    basic_unit_price: unitPrice,
    unit_rate_of_freight: Number(
      material.unit_rate_of_freight ?? material.freight ?? 0,
    ),
    basic_amount: basicAmount,
  };
};

export const filterGeneralPoCityDetailsByVendor = (
  cityDetails = [],
  vendorId,
) => {
  const selectedVendorId = Number(vendorId);
  if (!selectedVendorId) {
    return cityDetails;
  }

  return cityDetails
    .map((city) => {
      if (
        city.vendor_id != null &&
        Number(city.vendor_id) !== selectedVendorId
      ) {
        return null;
      }

      if (city.po_material_detail?.length) {
        return Number(city.vendor_id) === selectedVendorId ||
          city.vendor_id == null
          ? city
          : null;
      }

      const purchaseOrderSiteDetails = (city.purchase_order_site_details ?? [])
        .map((site) => ({
          ...site,
          po_vendor_detail: (site.po_vendor_detail ?? []).filter(
            (vendor) => Number(vendor.vendor_id) === selectedVendorId,
          ),
        }))
        .filter((site) => site.po_vendor_detail?.length);

      if (!purchaseOrderSiteDetails.length) {
        return null;
      }

      return {
        ...city,
        purchase_order_site_details: purchaseOrderSiteDetails,
      };
    })
    .filter(Boolean);
};

const buildSiteLocationFromCompany = (company = {}) => {
  const addressLine = formatPoAddress(
    company.registered_business_address_building,
    company.registered_business_address_area,
    company.registered_business_address_landmark,
  );

  return [
    {
      gst_number: company.gst_number ?? "",
      billing_details: {
        billing_address_line_1: addressLine === "N/A" ? "" : addressLine,
        billing_address_line_2: "",
        billing_city: company.registered_business_address_city ?? "",
        billing_state: company.registered_business_address_state ?? "",
        billing_pincode: company.registered_business_address_pincode ?? "",
        billing_keypersons: [],
      },
      shipping_details: {
        shipping_address_line_1: addressLine === "N/A" ? "" : addressLine,
        shipping_address_line_2: "",
        shipping_city: company.registered_business_address_city ?? "",
        shipping_state: company.registered_business_address_state ?? "",
        shipping_pincode: company.registered_business_address_pincode ?? "",
        shipping_keypersons: [],
      },
    },
  ];
};

const generalPoPdfRequestCache = new Map();

export async function fetchGeneralPoPdfData(id, vendorId, getRequest) {
  const cacheKey = `${id}-${vendorId}`;

  if (generalPoPdfRequestCache.has(cacheKey)) {
    return generalPoPdfRequestCache.get(cacheKey);
  }

  const requestPromise = (async () => {
    const response = await getRequest(
      `general-po-order/${id}/download-purchase-order-approval-pdf/?vendor_id=${vendorId}`,
    );

    const pdfPayload = response?.data?.data ?? response?.data ?? null;
    if (!pdfPayload || response?.data?.success === false) {
      return null;
    }

    return normalizeGeneralPoPdfData({ data: pdfPayload });
  })();

  generalPoPdfRequestCache.set(cacheKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    generalPoPdfRequestCache.delete(cacheKey);
  }
}

const buildRfqItemsFromCityDetails = (cityDetails = []) => {
  const items = [];

  cityDetails.forEach((city) => {
    if (city.po_material_detail?.length) {
      city.po_material_detail.forEach((material) => {
        items.push(mapPoMaterialToRfqItem(material));
      });
      return;
    }

    (city.purchase_order_site_details ?? []).forEach((site) => {
      (site.po_vendor_detail ?? []).forEach((vendor) => {
        (
          vendor.po_material_detail ??
          vendor.general_po_material_detail ??
          []
        ).forEach((material) => {
          items.push(mapPoMaterialToRfqItem(material));
        });
      });
    });
  });

  return items;
};

const getDefaultSiteLocation = () => ({
  gst_number: "",
  billing_details: {
    billing_address_line_1: "N/A",
    billing_address_line_2: "",
    billing_city: "",
    billing_state: "",
    billing_pincode: "",
    billing_keypersons: [],
  },
  shipping_details: {
    shipping_address_line_1: "N/A",
    shipping_address_line_2: "",
    shipping_city: "",
    shipping_state: "",
    shipping_pincode: "",
    shipping_keypersons: [],
  },
});

const buildSiteLocationFromCityDetails = (cityDetails = []) => {
  const site = cityDetails[0]?.purchase_order_site_details?.[0];
  if (!site) {
    return [getDefaultSiteLocation()];
  }

  const billing = site.billing_details ?? site.site_billing_details ?? {};
  const shipping = site.shipping_details ?? site.site_shipping_details ?? {};

  return [
    {
      gst_number: site.gst_number ?? billing.gst_number ?? "",
      billing_details: {
        billing_address_line_1: billing.billing_address_line_1 ?? "",
        billing_address_line_2: billing.billing_address_line_2 ?? "",
        billing_city: billing.billing_city ?? "",
        billing_state: billing.billing_state ?? "",
        billing_pincode: billing.billing_pincode ?? "",
        billing_keypersons: billing.billing_keypersons ?? [],
      },
      shipping_details: {
        shipping_address_line_1: shipping.shipping_address_line_1 ?? "",
        shipping_address_line_2: shipping.shipping_address_line_2 ?? "",
        shipping_city: shipping.shipping_city ?? "",
        shipping_state: shipping.shipping_state ?? "",
        shipping_pincode: shipping.shipping_pincode ?? "",
        shipping_keypersons: shipping.shipping_keypersons ?? [],
      },
    },
  ];
};

const computePoTotals = (rfqItem = [], raw = {}) => {
  if (Array.isArray(raw.total) && raw.total.length > 0) {
    return raw.total;
  }

  const basicSubTotal = rfqItem.reduce(
    (sum, item) => sum + (Number(item.basic_amount) || 0),
    0,
  );
  const totalWithGst =
    Number(raw.total_amount ?? raw.amount_with_gst) || basicSubTotal;
  const gstDiff = Math.max(totalWithGst - basicSubTotal, 0);
  const halfGst = gstDiff / 2;

  return [
    {
      basic_sub_total: basicSubTotal || totalWithGst,
      cgst: Number(raw.cgst) || halfGst,
      sgst: Number(raw.sgst) || halfGst,
      total: totalWithGst,
    },
  ];
};

const toDetailsArray = (value, fallback = {}) => {
  if (Array.isArray(value)) {
    return value.length ? value : [fallback];
  }
  if (value && typeof value === "object") {
    return [value];
  }
  return [fallback];
};

export function normalizeGeneralPoPdfData(apiResponse) {
  const raw = apiResponse?.data?.data ?? apiResponse?.data ?? apiResponse ?? {};

  let rfqItem = Array.isArray(raw.rfq_item) ? raw.rfq_item : [];
  if (!rfqItem.length) {
    rfqItem = buildRfqItemsFromCityDetails(raw.purchase_order_city_details);
  }
  if (!rfqItem.length && Array.isArray(raw.po_material_detail)) {
    rfqItem = raw.po_material_detail.map(mapPoMaterialToRfqItem);
  }

  let siteLocation = Array.isArray(raw.site_location) ? raw.site_location : [];
  if (!siteLocation.length) {
    siteLocation = buildSiteLocationFromCityDetails(
      raw.purchase_order_city_details,
    );
  }
  if (!siteLocation.length) {
    siteLocation = [getDefaultSiteLocation()];
  }

  const company = toDetailsArray(raw.company_details, raw.company ?? {})[0];
  if (
    siteLocation[0]?.billing_details?.billing_address_line_1 === "N/A" &&
    company?.name
  ) {
    siteLocation = buildSiteLocationFromCompany(company);
  }

  return {
    ...raw,
    company_details: toDetailsArray(raw.company_details, raw.company ?? {}),
    vendor_details: toDetailsArray(raw.vendor_details, raw.vendor ?? {}),
    rfq_item: rfqItem,
    site_location: siteLocation,
    total: computePoTotals(rfqItem, raw),
    is_diameter_wise: Boolean(raw.is_diameter_wise),
    is_freight_excluded: Boolean(raw.is_freight_excluded),
    payment_terms: raw.payment_terms ?? "",
    reference_number: raw.reference_number ?? raw.rfq_number ?? "",
  };
}

export { formatPoAddress };
